import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ListingsAPI } from '@certi/shared/api/listings/ListingsAPI'
import type { ListingResponse } from '@certi/shared/types'
import { FavoriteAPI } from '@certi/shared/api/user/FavoriteAPI'
import { FavoriteType } from '@certi/shared/types/types.user'
import { useAuth } from '@/hooks/useAuth'

interface ListingsQuery {
  page?: number
  size?: number
  sort?: ListingSort
  filters?: any
  search?: string
}

interface ListingSort {
  field: 'createdAt' | 'updatedAt' | 'price' | 'title'
  direction: 'asc' | 'desc'
}

interface FilterChip {
  id: string
  label: string
}

import SortDropdown from '@/components/marketplace/SortDropdown'
import ListingCard from '@/components/marketplace/ListingCard'
import HeroSlider, { type Campaign } from '@/components/marketplace/HeroSlider'
import { getMockListings } from '@/config/mockListings'
import { Pill } from '@/components/ui/Pill'

const isWornCondition = (condition: string) => ['GAME_WORN', 'WARMUP_WORN'].includes(condition)

export default function MarketplacePage() {
  const navigate = useNavigate()
  const { user, session } = useAuth()
  const [searchParams] = useSearchParams()
  const [listings, setListings] = useState<ListingResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [favorites, setFavorites] = useState<Set<string>>(new Set())

  // Search and filtering state (mobile-inspired)
  const searchText = searchParams.get('q') || ''
  const [selectedChip, setSelectedChip] = useState<string>('all')
  const [sort, setSort] = useState<ListingSort | undefined>({ field: 'createdAt', direction: 'desc' })
  const [currentPage, setCurrentPage] = useState(0)
  const [_totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [hasMore, setHasMore] = useState(false)

  // Hero slider campaigns
  const campaigns: Campaign[] = [
    {
      id: '1',
      headline: 'Authentic Game-Worn Jerseys',
      description: 'Own a piece of sports history with verified, game-worn jerseys from legendary athletes. Each item comes with certificate of authenticity.',
      ctaText: 'Shop Jerseys',
      ctaLink: '/marketplace',
      imageUrl: 'https://certi-prod-bucket-uploads.s3.amazonaws.com/media/hero1.jpeg',
      backgroundColor: '#f9fafb',
    },
    {
      id: '2',
      headline: 'Limited Edition Signed Memorabilia',
      description: 'Discover rare autographed items from your favorite sports stars. Every signature authenticated and verified by industry experts.',
      ctaText: 'Browse Collection',
      ctaLink: '/marketplace',
      imageUrl: 'https://certi-prod-bucket-uploads.s3.amazonaws.com/media/hero2.jpeg',
      backgroundColor: '#f9fafb',
    },
    {
      id: '3',
      headline: 'Vintage Sports Equipment',
      description: 'Classic gear from iconic moments in sports. From championship bats to historic footballs, find treasures that tell a story.',
      ctaText: 'Explore Now',
      ctaLink: '/marketplace',
      imageUrl: 'https://certi-prod-bucket-uploads.s3.amazonaws.com/media/hero3.jpeg',
      backgroundColor: '#f9fafb',
    },
    {
      id: '4',
      headline: 'Exclusive Championship Collectibles',
      description: 'Rare items from championship-winning seasons. Own memorabilia that celebrates the greatest moments in sports history.',
      ctaText: 'Shop Now',
      ctaLink: '/marketplace',
      imageUrl: 'https://certi-prod-bucket-uploads.s3.amazonaws.com/media/hero4.jpeg',
      backgroundColor: '#f9fafb',
    },
  ]

  // Filter chips data (mobile-inspired)
  const filterChips: FilterChip[] = [
    { id: 'all', label: 'All' },
    { id: 'jerseys', label: 'Jerseys' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'footwear', label: 'Footwear' },
    { id: 'accessories', label: 'Accessories' },
    { id: 'signed', label: 'Signed' },
    { id: 'ending', label: 'Ending Soon' }
  ]

  // Load user favorites on mount
  useEffect(() => {
    const loadFavorites = async () => {
      if (user?.id && session?.accessToken) {
        try {
          const favoriteIds = await FavoriteAPI.getUserFavoriteIds(
            user.id.toString(),
            FavoriteType.LISTING,
            session.accessToken
          )
          setFavorites(new Set(favoriteIds))
        } catch (err) {
          console.error('Failed to load favorites:', err)
        }
      }
    }
    loadFavorites()
  }, [user?.id, session?.accessToken])

  // Convert chip selection to filter parameters
  const chipFilters = useMemo(() => {
    switch (selectedChip) {
      case 'jerseys':
        return { category: 'CLOTHING' }
      case 'equipment':
        return { category: 'EQUIPMENT' }
      case 'footwear':
        return { category: 'FOOTWEAR' }
      case 'accessories':
        return { category: 'ACCESSORIES' }
      case 'signed':
        return { tags: ['AUTOGRAPHED'] }
      case 'ending':
        return { endingSoon: true }
      default:
        return {}
    }
  }, [selectedChip])

  const fetchListings = async (reset = false) => {
    try {
      setLoading(true)
      setError(null)

      const query: ListingsQuery = {
        page: reset ? 0 : currentPage,
        size: 20,
        search: searchText || undefined,
        sort,
        filters: chipFilters
      }

      const response = await ListingsAPI.fetchListings(query)

      const enableMock = (import.meta as any).env?.VITE_ENABLE_MOCK_LISTINGS === 'true'
      const incoming = response.listings || []
      let effective = (incoming.length === 0 && enableMock) ? getMockListings() : incoming
      // If using mocks, apply client-side filter for search text
      if (enableMock && (searchText || '').trim().length > 0) {
        const q = searchText.trim().toLowerCase()
        effective = effective.filter(l => (
          (l.title && l.title.toLowerCase().includes(q)) ||
          (l.playerName && l.playerName.toLowerCase().includes(q)) ||
          (l.teamOrClub && l.teamOrClub.toLowerCase().includes(q)) ||
          (l.slug && l.slug.toLowerCase().includes(q))
        ))
      }

      if (reset) {
        setListings(effective)
        setCurrentPage(0)
      } else {
        setListings(prev => [...prev, ...effective])
      }
      
      setTotalPages(response.totalPages)
      setTotalElements(effective.length > 0 ? Math.max(response.totalElements, effective.length) : response.totalElements)
      setHasMore(!response.last)
      
    } catch (err) {
      console.error('Failed to fetch listings:', err)
      const enableMock = (import.meta as any).env?.VITE_ENABLE_MOCK_LISTINGS === 'true'
      if (enableMock) {
        let mock = getMockListings()
        if ((searchText || '').trim().length > 0) {
          const q = searchText.trim().toLowerCase()
          mock = mock.filter(l => (
            (l.title && l.title.toLowerCase().includes(q)) ||
            (l.playerName && l.playerName.toLowerCase().includes(q)) ||
            (l.teamOrClub && l.teamOrClub.toLowerCase().includes(q)) ||
            (l.slug && l.slug.toLowerCase().includes(q))
          ))
        }
        if (reset) {
          setListings(mock)
          setCurrentPage(0)
        } else {
          setListings(prev => [...prev, ...mock])
        }
        setTotalPages(1)
        setTotalElements(mock.length)
        setHasMore(false)
        setError(null)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load listings')
      }
    } finally {
      setLoading(false)
    }
  }

  // Initial load and reload on filter changes (mobile-inspired)
  useEffect(() => {
    setCurrentPage(0)
    fetchListings(true)
  }, [searchText, chipFilters, sort])

  // Load more when page changes
  useEffect(() => {
    if (currentPage > 0) {
      fetchListings(false)
    }
  }, [currentPage])

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setCurrentPage(prev => prev + 1)
    }
  }

  const handleListingClick = (listing: ListingResponse) => {
    if (listing.slug) {
      navigate(`/marketplace/${listing.slug}`)
    } else {
      navigate(`/marketplace/${listing.id}`)
    }
  }

  const handleToggleFavorite = async (listingId: string) => {
    if (!user?.id || !session?.accessToken) {
      // Not logged in - navigate to login
      navigate('/login')
      return
    }

    // Optimistic update
    setFavorites(prev => {
      const newFavorites = new Set(prev)
      if (newFavorites.has(listingId)) {
        newFavorites.delete(listingId)
      } else {
        newFavorites.add(listingId)
      }
      return newFavorites
    })

    try {
      const isFavorited = await FavoriteAPI.toggleFavorite(
        user.id.toString(),
        { targetType: FavoriteType.LISTING, targetId: listingId },
        session.accessToken
      )

      // Update state with actual backend response
      setFavorites(prev => {
        const newFavorites = new Set(prev)
        if (isFavorited) {
          newFavorites.add(listingId)
        } else {
          newFavorites.delete(listingId)
        }
        return newFavorites
      })
    } catch (err) {
      console.error('Failed to toggle favorite:', err)
      // Revert optimistic update on error
      setFavorites(prev => {
        const newFavorites = new Set(prev)
        if (newFavorites.has(listingId)) {
          newFavorites.delete(listingId)
        } else {
          newFavorites.add(listingId)
        }
        return newFavorites
      })
    }
  }


  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => fetchListings(true)}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4">

      {/* Hero Slider */}
      <HeroSlider campaigns={campaigns} autoPlayInterval={3500} />

      {/* Filter Chips - Mobile Style */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {filterChips.map((chip) => {
          const isSelected = selectedChip === chip.id
          return (
            <button
              key={chip.id}
              onClick={() => setSelectedChip(chip.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                isSelected
                  ? 'bg-brand-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {chip.label}
            </button>
          )
        })}
      </div>

      {/* Divider - Mobile Style */}
      <div className="border-t border-gray-200 mb-6"></div>

      {/* Results Toolbar - Mobile Style */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-base font-medium text-gray-900">
          {totalElements > 0 ? (
            `${totalElements} items found`
          ) : (
            'No items found'
          )}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Sort by:</span>
          <SortDropdown sort={sort} onSortChange={setSort} />
        </div>
      </div>

      {/* Featured Section (from live listings) */}
      {listings.filter(l => l.mediaUrls && l.mediaUrls.length > 0).length > 0 && (
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Featured Sports Memorabilia</h2>
            <button 
              onClick={() => navigate('/marketplace')}
              className="text-sm font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
            >
              View All
            </button>
          </div>

          <div className="flex gap-4 overflow-x-auto pb-4 mb-6">
            {listings
              .filter(l => l.mediaUrls && l.mediaUrls.length > 0)
              .slice(0, 2)
              .map((l) => (
                <div
                  key={l.id}
                  onClick={() => handleListingClick(l)}
                  className="flex-shrink-0 bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow w-80"
                >
                  <div className="relative aspect-[4/5] bg-gray-100 overflow-hidden">
                    <img
                      src={l.mediaUrls[0]}
                      alt={l.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        target.parentElement!.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center bg-gray-100">
                            <svg class="w-12 h-12 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                            </svg>
                          </div>
                        `
                      }}
                    />
                    <div className="absolute top-2 left-2 flex gap-1">
                      {isWornCondition(l.condition) && <Pill variant="solidDark">WORN</Pill>}
                      {l.tags?.includes('AUTOGRAPHED') && (
                        <Pill variant="solidDark">SIGNED</Pill>
                      )}
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{l.title}</h3>
                    <div className="text-xs text-gray-600 mt-1">price</div>
                    <div className="text-lg font-bold text-gray-900">${Number(l.price || 0).toFixed(2)}</div>
                    <div className="text-xs text-gray-600 capitalize">
                      {`${l.category.replace(/_/g, ' ').toLowerCase()} · ${l.condition.replace(/_/g, ' ').toLowerCase()}`}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Section Header - Mobile Style */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">All Listings</h2>
        <button 
          onClick={() => console.log('View all listings')}
          className="text-sm font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
        >
          View All
        </button>
      </div>

      {/* Listings Grid - Desktop Optimized */}
      {listings.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 mb-6">
            {listings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                isFavorite={favorites.has(listing.id)}
                onPress={() => handleListingClick(listing)}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center mb-8">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="btn btn-secondary"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      ) : !loading ? (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No listings found</h3>
          <p className="text-gray-500">Try adjusting your search or filters</p>
        </div>
      ) : null}

      {/* Loading State */}
      {loading && listings.length === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse">
              <div className="flex gap-4">
                <div className="w-22 h-22 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
